import { useCallback, useEffect, useId, useRef, useState } from 'react'

/**
 * El recorrido guiado de una pantalla: cuatro a seis paradas que señalan un
 * elemento real y explican para qué sirve.
 *
 * SE APOYA EN EL DOM QUE YA EXISTE, no en una copia de la pantalla. Un recorrido
 * con capturas o con texto que describe botones envejece el mismo día en que
 * alguien cambia la interfaz, y nadie se entera hasta que un alumno busca un
 * botón que ya no está. Aquí cada paso trae un selector: si el elemento está, se
 * resalta el de verdad; si no está, EL PASO SE SALTA. Un recorrido que revienta
 * porque cambió una clase de CSS es peor que no tener recorrido.
 *
 * Y ESE «SI NO ESTÁ, SE SALTA» NO ES DEFENSA GENÉRICA: en este sistema la misma
 * pantalla enseña cosas distintas según los permisos de quien mira, y la de
 * disponibilidad ni siquiera pinta bloques mientras la escuela no cargue su
 * horario. Que falte un elemento es lo normal, no la excepción.
 *
 * SE MUESTRA UNA VEZ. Repetirlo en cada visita convierte la ayuda en un
 * estorbo; por eso la marca vive en `localStorage`. Se relanza a mano con
 * `relanzarTour()`, que es lo que debe llamar un enlace de «Ver el recorrido
 * otra vez».
 */

export interface PasoDelTour {
  /** Selector CSS del elemento que se resalta. Si no existe, el paso se salta. */
  selector: string
  titulo: string
  texto: string
}

/** Versionada: cambiar los pasos obliga a volver a mostrarlo, y eso se decide
    subiendo el número, no borrando la clave a mano en cada navegador. */
export const CLAVE_TOUR_VISTO = 'tour-visto-v1'

/** Evento propio para relanzar sin desmontar nada. Ver `relanzarTour()`. */
const EVENTO_RELANZAR = 'cubiculos:relanzar-tour'

/** Ancho de la tarjeta, en píxeles, para no dejarla salirse de la ventana. */
const ANCHO_TARJETA = 320

/** Separación entre el elemento resaltado y la tarjeta. */
const MARGEN = 12

/**
 * Vuelve a lanzar el recorrido que ya se había visto.
 *
 * Borra la marca Y avisa por un evento: sin el evento, quien ya tiene el
 * componente montado no se enteraría del cambio en `localStorage` —esa API no
 * notifica a la misma pestaña— y habría que recargar la página para verlo.
 */
export function relanzarTour(clave: string = CLAVE_TOUR_VISTO): void {
  olvidarMarca(clave)
  window.dispatchEvent(new Event(EVENTO_RELANZAR))
}

/* `localStorage` lanza excepción en modo privado de algunos navegadores y
   cuando la cuota está llena. Nunca vale tumbar la pantalla por no poder
   recordar si ya se vio un recorrido: en el peor caso se muestra de más. */
function yaSeVio(clave: string): boolean {
  try {
    return window.localStorage.getItem(clave) !== null
  } catch {
    return false
  }
}

function recordarMarca(clave: string): void {
  try {
    window.localStorage.setItem(clave, 'sí')
  } catch {
    /* Sin almacenamiento el recorrido se repetirá; no es motivo para fallar. */
  }
}

function olvidarMarca(clave: string): void {
  try {
    window.localStorage.removeItem(clave)
  } catch {
    /* Igual que arriba: es una preferencia, no un dato. */
  }
}

/** Quien pide menos animación no recibe desplazamientos animados ni transiciones. */
function prefiereMenosMovimiento(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

interface PropiedadesTour {
  pasos: PasoDelTour[]
  /** `false` deja el recorrido disponible solo a petición, con `relanzarTour()`. */
  automatico?: boolean
  /** Permite que dos pantallas tengan recorridos independientes. */
  clave?: string
}

export function Tour({
  pasos,
  automatico = true,
  clave = CLAVE_TOUR_VISTO,
}: PropiedadesTour) {
  /** `null` es cerrado. No se usa un booleano aparte: dos estados que pueden
      contradecirse producen el recorrido abierto sin paso que mostrar. */
  const [indice, setIndice] = useState<number | null>(null)
  const [marco, setMarco] = useState<DOMRect | null>(null)
  const refTarjeta = useRef<HTMLDivElement | null>(null)
  /** Quién tenía el foco antes de abrir, para devolvérselo al cerrar. */
  const refFocoPrevio = useRef<HTMLElement | null>(null)
  const idTitulo = useId()
  const idTexto = useId()

  /**
   * El primer paso hacia adelante (o hacia atrás) cuyo elemento existe AHORA.
   * Se consulta el DOM en cada salto, no una vez al abrir: entre un paso y otro
   * la pantalla puede haber cargado datos y hecho aparecer una tabla.
   */
  const buscarPaso = useCallback(
    (desde: number, direccion: 1 | -1): number | null => {
      for (let i = desde; i >= 0 && i < pasos.length; i += direccion) {
        const paso = pasos[i]
        if (paso !== undefined && document.querySelector(paso.selector) !== null) {
          return i
        }
      }
      return null
    },
    [pasos],
  )

  const cerrar = useCallback(() => {
    setIndice(null)
    setMarco(null)

    // EL FOCO VUELVE A DONDE ESTABA. El recorrido se lo lleva a la tarjeta en
    // cada paso, así que al desmontarla el foco caería al `body` y quien navega
    // con teclado quedaría en la nada, sin saber dónde retomar. Se devuelve al
    // elemento que lo tenía cuando el recorrido se abrió — normalmente el botón
    // que lo lanzó. Lo señaló la revisión de Codex.
    const previo = refFocoPrevio.current
    refFocoPrevio.current = null
    if (previo !== null && previo.isConnected) previo.focus()
    // Se marca como visto también al saltarlo: quien lo cerró ya dijo que no lo
    // quiere, y devolvérselo en la siguiente visita es no haberlo escuchado.
    recordarMarca(clave)
  }, [clave])

  const abrir = useCallback(() => {
    const primero = buscarPaso(0, 1)
    // Se guarda ANTES de mover el foco a la tarjeta.
    refFocoPrevio.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    // Ni un solo elemento en pantalla: no se abre una tarjeta que señala al
    // vacío. No se marca como visto, así que se intentará en la próxima visita,
    // cuando quizá la pantalla ya tenga datos.
    if (primero === null) return
    setIndice(primero)
  }, [buscarPaso])

  /* Arranque automático. El `setTimeout` de 0 no es superstición: deja que
     TanStack Query pinte lo que ya tiene en caché antes de buscar selectores,
     que si no darían todos por inexistentes en el primer render. */
  useEffect(() => {
    if (!automatico || yaSeVio(clave)) return
    const temporizador = window.setTimeout(abrir, 0)
    return () => window.clearTimeout(temporizador)
  }, [automatico, clave, abrir])

  /* Relanzado a mano desde cualquier parte de la aplicación. */
  useEffect(() => {
    window.addEventListener(EVENTO_RELANZAR, abrir)
    return () => window.removeEventListener(EVENTO_RELANZAR, abrir)
  }, [abrir])

  const pasoActual = indice === null ? undefined : pasos[indice]
  const selectorActual = pasoActual?.selector ?? null

  /* El recuadro del elemento resaltado. Depende del SELECTOR y no del arreglo
     de pasos: si dependiera del arreglo, un padre que lo construye en línea
     dispararía este efecto en cada render suyo. */
  useEffect(() => {
    if (selectorActual === null) return

    const elemento = document.querySelector(selectorActual)
    if (elemento === null) {
      // Desapareció entre el salto y la medición. Se cierra en vez de dejar la
      // tarjeta señalando a la nada.
      cerrar()
      return
    }

    elemento.scrollIntoView({
      behavior: prefiereMenosMovimiento() ? 'auto' : 'smooth',
      block: 'center',
      inline: 'nearest',
    })

    const medir = () => setMarco(elemento.getBoundingClientRect())
    medir()

    // La captura en `scroll` recoge también el desplazamiento de contenedores
    // internos, como la envoltura con scroll horizontal de las tablas.
    window.addEventListener('resize', medir)
    window.addEventListener('scroll', medir, true)

    // Y SI EL ELEMENTO DESAPARECE MIENTRAS EL RECORRIDO ESTÁ ABIERTO. Pasa de
    // verdad: TanStack Query refresca una lista y el nodo resaltado se
    // desmonta. Sin esto, la comprobación de existencia solo corría al cambiar
    // de paso, así que la tarjeta se quedaba señalando un recuadro que ya no
    // corresponde a nada. Lo señaló la revisión de Codex.
    const vigilante = new MutationObserver(() => {
      if (!elemento.isConnected) cerrar()
      else medir()
    })
    vigilante.observe(document.body, { childList: true, subtree: true })

    return () => {
      window.removeEventListener('resize', medir)
      window.removeEventListener('scroll', medir, true)
      vigilante.disconnect()
    }
  }, [selectorActual, cerrar])

  /* Escape cierra, esté donde esté el foco. */
  useEffect(() => {
    if (indice === null) return

    function alTeclado(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return
      evento.stopPropagation()
      cerrar()
    }

    document.addEventListener('keydown', alTeclado)
    return () => document.removeEventListener('keydown', alTeclado)
  }, [indice, cerrar])

  /* El foco viaja a la tarjeta en cada paso. Sin esto, quien navega con teclado
     seguiría con el foco donde lo dejó y no oiría nada de lo que el recorrido
     está diciendo. */
  useEffect(() => {
    if (indice === null) return
    refTarjeta.current?.focus()
  }, [indice])

  if (indice === null || pasoActual === undefined) return null

  const anterior = buscarPaso(indice - 1, -1)
  const siguiente = buscarPaso(indice + 1, 1)
  // Se numera sobre los pasos que EXISTEN, no sobre el arreglo completo: decir
  // «paso 2 de 6» cuando dos se saltaron es mentir sobre lo que falta.
  const visibles = pasos.filter(
    (paso) => document.querySelector(paso.selector) !== null,
  ).length
  const posicion =
    pasos.slice(0, indice + 1).filter(
      (paso) => document.querySelector(paso.selector) !== null,
    ).length

  // Sin un solo paso en pantalla no hay recorrido que mostrar, y pintar
  // «Paso 0 de 0» sobre una tarjeta que no señala nada es peor que no pintar
  // nada. El cierre se hace en el efecto de abajo, no aquí: cambiar el estado
  // durante el render es un error de React. Lo señaló la revisión de Codex.
  if (visibles === 0) return null

  return (
    <>
      <ESTILOS />
      {marco !== null && (
        <div
          className="tour__realce"
          aria-hidden="true"
          style={{
            top: `${marco.top}px`,
            left: `${marco.left}px`,
            width: `${marco.width}px`,
            height: `${marco.height}px`,
          }}
        />
      )}

      <div
        className="tour__tarjeta"
        ref={refTarjeta}
        role="dialog"
        // No es modal: la página sigue debajo, se puede leer y se puede usar. El
        // recorrido explica la pantalla, así que taparla sería contraproducente.
        aria-labelledby={idTitulo}
        aria-describedby={idTexto}
        tabIndex={-1}
        style={colocarTarjeta(marco)}
      >
        <p className="tour__conteo">
          Paso {posicion} de {visibles}
        </p>
        <h2 className="tour__titulo" id={idTitulo}>
          {pasoActual.titulo}
        </h2>
        <p className="tour__texto" id={idTexto}>
          {pasoActual.texto}
        </p>

        <div className="tour__acciones">
          <button
            type="button"
            className="boton"
            onClick={() => {
              if (anterior !== null) setIndice(anterior)
            }}
            disabled={anterior === null}
          >
            Anterior
          </button>
          <button
            type="button"
            className="boton boton--primario"
            onClick={() => {
              // Sin siguiente, «Terminar»: llegar al final es completarlo.
              if (siguiente === null) cerrar()
              else setIndice(siguiente)
            }}
          >
            {siguiente === null ? 'Terminar' : 'Siguiente'}
          </button>
          <button type="button" className="boton tour__saltar" onClick={cerrar}>
            Saltar el recorrido
          </button>
        </div>
      </div>
    </>
  )
}

/**
 * Dónde va la tarjeta: debajo del elemento si cabe, encima si no, y centrada en
 * la ventana mientras no hay medida. Se calcula en píxeles fijos porque el
 * recuadro que devuelve `getBoundingClientRect()` ya es relativo a la ventana.
 */
function colocarTarjeta(marco: DOMRect | null): {
  top: string
  left: string
} {
  if (marco === null) {
    return { top: '50%', left: `calc(50% - ${ANCHO_TARJETA / 2}px)` }
  }

  const izquierda = Math.min(
    Math.max(MARGEN, marco.left + marco.width / 2 - ANCHO_TARJETA / 2),
    Math.max(MARGEN, window.innerWidth - ANCHO_TARJETA - MARGEN),
  )
  // Se reserva una altura aproximada para decidir el lado; la tarjeta tiene tres
  // renglones y una fila de botones, así que 220px es una cota holgada.
  const altoEstimado = 220
  const cabeDebajo = marco.bottom + altoEstimado + MARGEN <= window.innerHeight
  const arriba = cabeDebajo
    ? marco.bottom + MARGEN
    : Math.max(MARGEN, marco.top - altoEstimado - MARGEN)

  return { top: `${arriba}px`, left: `${izquierda}px` }
}

/**
 * Estilos del recorrido, montados una sola vez gracias a `precedence`.
 *
 * NO HAY VELO OSCURO sobre la página, y es a propósito por dos razones: un velo
 * exige un color con transparencia que no está en `tokens.css` —y en este
 * proyecto ningún color literal vive fuera de ahí—, y además taparía justo lo
 * que el recorrido está explicando. El realce es un anillo con el ocre
 * institucional, que se ve de reojo sin apagar nada.
 */
function ESTILOS() {
  return (
    <style href="cubiculos-tour" precedence="componente">{`
      .tour__realce {
        position: fixed;
        z-index: 40;
        /* No intercepta clics: la página sigue siendo usable con el recorrido
           abierto, que es medio punto de un recorrido sobre la pantalla real. */
        pointer-events: none;
        border-radius: var(--radio-sm);
        outline: 3px solid var(--color-acento);
        outline-offset: 4px;
        box-shadow: var(--sombra-md);
        transition: top 150ms ease, left 150ms ease, width 150ms ease,
          height 150ms ease;
      }

      .tour__tarjeta {
        position: fixed;
        z-index: 41;
        width: min(${ANCHO_TARJETA}px, calc(100vw - 2rem));
        padding: var(--esp-4);
        border: 1px solid var(--borde-normal);
        border-top: var(--esp-1) solid var(--color-acento);
        border-radius: var(--radio-md);
        background-color: var(--superficie-elevada);
        color: var(--texto-normal);
        box-shadow: var(--sombra-md);
      }

      .tour__conteo {
        font-size: var(--texto-xs);
        color: var(--texto-tenue);
        font-variant-numeric: tabular-nums;
      }

      .tour__titulo {
        font-size: var(--texto-md);
        color: var(--texto-fuerte);
        margin-top: var(--esp-1);
      }

      .tour__texto {
        font-size: var(--texto-sm);
        margin-top: var(--esp-2);
      }

      .tour__acciones {
        display: flex;
        flex-wrap: wrap;
        gap: var(--esp-2);
        margin-top: var(--esp-4);
      }

      /* Saltar es legítimo, pero no compite con la acción principal. */
      .tour__saltar { margin-left: auto; }

      @media (prefers-reduced-motion: reduce) {
        .tour__realce { transition: none; }
      }
    `}</style>
  )
}
